import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { ICalendarEntry, ICalendarEvent } from '@gik/calendar/models/Calendar';
import { Avatar, Button, Decoder, HTMLParser, Image, LineClamp, LoadingSpinner, SvgIcon, TabbedView } from '@gik/ui';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { useInkind } from '@gik/api/inkinds/inkind';
import { useServices } from '@gik/api/calendar/service';
import { useCalendarEventTypes } from '@gik/api/calendar/calendarEventType';
import noop from '@gik/core/utils/noop';
import { useServiceCategories } from '@gik/api/calendar/serviceProductBundle';
import {
  canUserEditPage,
  inkindPageOwnerIsRecipient,
  isInkindPageOwnerOrOrganizer,
} from '@gik/inkind-page/utils/inkindPagePermissions';
import { useUserStore } from '@gik/core/store/UserStore';
import { renderPortal } from '@gik/core/utils/RenderPortal';
import { getRequestDetailsTabs } from '@gik/calendar/utils/CalendarClaimUtils';
import CalendarClaimHeader from './CalendarClaimHeader';
import { CalendarUtil } from '@gik/calendar';
import { isInPastAllTimezones } from '@gik/core/utils/DateTimeUtils';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { translationKeys } from './i18n/en';
import { CalendarEventTypeIds } from '@gik/calendar/models/CalendarEventTypes';
import { openUserInfoModal } from '@gik/user-profile/components/UserInfoModal/UserInfoModal';
import { BaseUserWithAvatar } from '@gik/core/models/gik/User';

export interface ICalendarClaimViewProps extends React.HTMLAttributes<HTMLDivElement> {
  event?: ICalendarEvent;
  entry: ICalendarEntry;
  buttonsPortal?: () => HTMLElement;
  onClose?(): void;
  onFailed?(): void;
  onSuccess?(): void;
  onEditSuccess?(): void;
  onUnclaim?(): void;
  onRefreshCalendar?(): void;
}

interface ISupporterInfoSectionProps {
  entry: ICalendarEntry;
  bottomBorder?: boolean;
}

function SupporterInfoSection({ entry, bottomBorder = false }: ISupporterInfoSectionProps) {
  const bem = bemBlock('calendar-claim-event');

  function openSupporterInfoModal(supporter: BaseUserWithAvatar) {
    openUserInfoModal(supporter);
  }

  let claimerFullname: string = null;
  if (entry.claim) {
    claimerFullname = entry.claim.supporter.firstName;
    if (entry.claim.supporter.lastName) {
      claimerFullname += ' ' + entry.claim.supporter.lastName;
    }
  }

  return (
    <section className={bem('event-claim', [{ 'bottom-border': bottomBorder }])}>
      <Avatar imageUrl={entry.claim.supporter.avatarThumbSmallURL} />
      <div>
        <span className={bem('provided-by')}>Provided by</span>
        <Button
          className={bem('provided-by-name')}
          variant={'default-extra-dark-link'}
          onClick={() => openSupporterInfoModal(entry.claim.supporter)}
        >
          <Decoder text={claimerFullname} />
        </Button>
      </div>
    </section>
  );
}

export default function CalendarClaimView({
  entry,
  event,
  className,
  buttonsPortal,
  onClose = noop,
  ...otherProps
}: ICalendarClaimViewProps): React.ReactElement {
  const bem = bemBlock('calendar-claim-event');
  const { t } = useTranslation();

  const claim = entry.claim;

  const [buttonPortalRef, setButtonPortalRef] = React.useState<HTMLElement>();

  const { data: calendarEventTypes } = useCalendarEventTypes();
  const { data: services } = useServices();
  const { data: serviceCategories } = useServiceCategories();

  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: inkind } = useInkind(inkindRouteId);

  const eventType = calendarEventTypes?.find(type => type.id === entry.typeId);

  const userId = useUserStore(state => state.id);

  const isOrganizer = isInkindPageOwnerOrOrganizer(inkind, userId);
  const canEdit = canUserEditPage(inkind, userId);
  const isLoading = !calendarEventTypes || !services || !serviceCategories || !buttonPortalRef;

  const isClaimer = entry.claim?.supporter?.id === userId;
  const isPastEvent = isInPastAllTimezones(moment.utc(event.startsAt));

  React.useEffect(() => {
    // trigger a rerender when the button portal resolves to an html element
    setButtonPortalRef(buttonsPortal());
  }, [buttonPortalRef, buttonsPortal]);

  if (isLoading) {
    return <LoadingSpinner center />;
  }

  const requestDetailTabs = getRequestDetailsTabs(entry, isOrganizer, isClaimer);

  function hasContactInformation(typeId: number) {
    return (
      typeId === CalendarEventTypeIds.PetCare ||
      typeId === CalendarEventTypeIds.ChildCare ||
      typeId === CalendarEventTypeIds.SeniorCare ||
      typeId === CalendarEventTypeIds.HomeServices ||
      typeId === CalendarEventTypeIds.Transportation
    );
  }

  const contactInfo = hasContactInformation(entry.typeId);

  const petCareEventType = calendarEventTypes.find(item => item.id === CalendarEventTypeIds.PetCare);
  const petCareType = petCareEventType?.acf.pet_care_types?.find(item => item.id === entry.petCareTypeId?.toString());

  function renderlabel() {
    return (
      <>
        {entry.typeId === CalendarEventTypeIds.SeniorCare &&
          entry.claim.serviceSlug === 'phone-call-adult-senior-care' && (
            <span className={bem('claim-type-title')}>Phone Call</span>
          )}
        {petCareType && entry.typeId === CalendarEventTypeIds.PetCare && (
          <span className={bem('claim-type-title')}>{petCareType?.name} </span>
        )}
        {!contactInfo && (
          <p className={bem('claim-description')}>
            <HTMLParser rawHtml={claim.description} />
          </p>
        )}
        {(isOrganizer || isClaimer) && (
          <>
            {contactInfo && claim.description && (
              <>
                <span className={bem('claim-description-title')}>Contact Info: </span>
                <p className={bem('claim-description')}>
                  <HTMLParser rawHtml={claim.description} />
                </p>
              </>
            )}
            {entry.typeId === CalendarEventTypeIds.Transportation && claim.carType && (
              <>
                <span className={bem('claim-description-title')}>
                  {isClaimer ? 'You' : 'They'}&apos;ll be arriving in:{' '}
                </span>
                <p className={bem('claim-description')}>
                  <HTMLParser rawHtml={claim.carType} />
                </p>
              </>
            )}
          </>
        )}
      </>
    );
  }

  function handleEditEvent() {
    onClose();
    setTimeout(() => {
      CalendarUtil.edit(event, entry);
    }, 500);
  }

  function handleEditClaim() {
    onClose();
    setTimeout(() => {
      CalendarUtil.claim(event, entry);
    }, 500);
  }

  let _buttons = (
    <div className={bem('buttons')}>
      <section>
        {isClaimer && !isPastEvent && (
          <Button variant="secondary" onClick={handleEditClaim} prepend={<SvgIcon name="Pencil" />}>
            Edit Claim
          </Button>
        )}
      </section>
      <section>
        {canEdit && !isPastEvent && (
          <Button variant="secondary" onClick={handleEditEvent} prepend={<SvgIcon name="Pencil" />}>
            Edit Request
          </Button>
        )}
      </section>
    </div>
  );

  if (canEdit && !isClaimer && !isPastEvent) {
    _buttons = (
      <>
        <Button wide variant="secondary" onClick={handleEditEvent} prepend={<SvgIcon name="Pencil" />}>
          Edit Request
        </Button>
      </>
    );
  }

  if ((!isOrganizer && !isClaimer) || entry.claim.serviceCategorySlug === 'gift-card') {
    _buttons = (
      <>
        <Button wide variant="primary" onClick={onClose}>
          Close
        </Button>
      </>
    );
  }

  const cartItem = entry.claim?.cartItems?.length ? entry.claim.cartItems[0] : null;

  const customMoreButton = (clamped: boolean) => (
    <Button variant="secondary" size="xs" pill className={bem('btn-more')}>
      {clamped && t(translationKeys.moreButton)}
      {!clamped && t(translationKeys.lessButton)}
    </Button>
  );

  // TODO: only show note if there is a page organizer that is also the recipient of the inkind page.
  const showNotetoRecipient = isOrganizer && inkindPageOwnerIsRecipient(inkind);

  return (
    <div className={bem(null, null, [className, `gik-calendar-event-type-theme--${eventType?.slug}`])} {...otherProps}>
      <CalendarClaimHeader entry={entry} event={event} eventType={eventType} />

      {entry.claim.serviceCategorySlug === 'gift-card' ? (
        <>
          <div className={bem('giftcard-view')}>
            <div>
              <Image src={cartItem?.imageUrl} />
              <span className={bem('giftcard-product-name')}>
                <HTMLParser rawHtml={cartItem.productName} />
              </span>
            </div>
            <main>
              <SupporterInfoSection entry={entry} />
            </main>
          </div>
        </>
      ) : (
        <>
          <main>
            <SupporterInfoSection entry={entry} bottomBorder />
            {renderlabel()}
            {showNotetoRecipient && claim.note && (
              <div className={bem('notes')}>
                <span className={bem('notes-title')}>PERSONAL NOTE</span>

                <div className={bem('notes-description')}>
                  <LineClamp customButton={customMoreButton} lines={2} fadeOut={false} horizontal>
                    <HTMLParser rawHtml={claim.note} />
                  </LineClamp>
                </div>
              </div>
            )}

            <TabbedView tabs={requestDetailTabs} />
          </main>
        </>
      )}

      {renderPortal(_buttons, buttonsPortal)}
      {/* {claimConflict && <ClaimFailedMessage />} */}
    </div>
  );
}
